import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { getUserService } from '@src/container';
import type { UserDTO } from '@src/application/users/dto/user.dto';

/**
 * Hook: useUnbanUser
 * Unbans a user (Admin only)
 * 
 * @param options - React Query mutation options
 * @returns Mutation result for unbanning user
 * 
 * @example
 * ```typescript
 * const { mutate: unbanUser, isPending, error } = useUnbanUser({
 *     onSuccess: (unbannedUser) => {
 *         console.log('User unbanned:', unbannedUser);
 *     }
 * });
 * 
 * // Usage
 * unbanUser('user-id-123');
 * ```
 */
export function useUnbanUser(
    options?: Omit<
        UseMutationOptions<UserDTO, Error, string>,
        'mutationFn'
    >
) {
    const userService = getUserService();
    const queryClient = useQueryClient();

    // Extract user's onSuccess before creating mutation
    const { onSuccess: userOnSuccess, ...restOptions } = options || {};

    return useMutation({
        ...restOptions,
        mutationFn: async (userId: string) => {
            return await userService.unban(userId);
        },
        onSuccess: async (data, variables, context, extra) => {
            // Invalidate and refetch users list
            await queryClient.invalidateQueries({ queryKey: ['users'] });

            // Update the specific user query
            queryClient.setQueryData(['users', variables], data);

            // Call user's onSuccess callback if provided
            if (userOnSuccess) {
                await userOnSuccess(data, variables, context, extra);
            }
        },
    });
}
